import { useEffect, useRef, useState, type RefObject } from 'react';
import { REMOTE_DATASETS, type RemoteDataset } from '../samples/catalog';

interface DatasetPickerProps {
  open: boolean;
  busy: boolean;
  /** Viewport-relative position; the picker renders position:fixed at this
   * spot (portaled to body) so the toolbar's overflow-hidden can't clip it. */
  anchor: { right: number; top: number };
  /** The toggle button's container — clicks inside it are the toggle's job
   * to handle, not the outside-click dismisser's. */
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onPick: (dataset: RemoteDataset) => void;
  onCustomUrl: (url: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function DatasetPicker({
  open,
  busy,
  anchor,
  triggerRef,
  onClose,
  onPick,
  onCustomUrl,
}: DatasetPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [customUrl, setCustomUrl] = useState('');

  const submitCustom = () => {
    const trimmed = customUrl.trim();
    if (!trimmed || busy) return;
    onCustomUrl(trimmed);
    setCustomUrl('');
    onClose();
  };

  // External-system effect: dismiss on outside-click. Listening on document
  // is the conventional way to catch every click; React doesn't own a
  // global click stream.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onClose, triggerRef]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className="fixed z-50 w-80 rounded border border-[#4a4a4a] bg-[#3c3f41] p-3 shadow-xl"
      style={{ right: anchor.right, top: anchor.top }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] font-semibold text-[#e8e8e8]">Curated datasets</span>
        <span className="text-[10px] text-[#808080]">fetched from raw.githubusercontent.com</span>
      </div>
      <div className="flex flex-col gap-1.5">
        {REMOTE_DATASETS.map((ds) => (
          <button
            key={ds.id}
            type="button"
            onClick={() => onPick(ds)}
            disabled={busy}
            className="group flex flex-col items-start gap-0.5 rounded border border-[#323232] bg-[#2b2b2b] p-2 text-left transition-colors hover:border-[#499c54] hover:bg-[#4c5052] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="flex w-full items-center justify-between gap-2">
              <span className="truncate text-[12px] font-semibold text-[#e8e8e8]">{ds.name}</span>
              <span className="shrink-0 font-mono text-[10px] text-[#808080]">
                ~{formatBytes(ds.bytes)} · {ds.license}
              </span>
            </div>
            <span className="text-[11px] leading-snug text-[#bbbbbb]">{ds.description}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 border-t border-[#323232] pt-3">
        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#808080]">
          Or paste a URL
        </label>
        <div className="flex gap-1">
          <input
            type="url"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCustom();
            }}
            placeholder="github.com/owner/repo/blob/main/file.db"
            disabled={busy}
            className="min-w-0 flex-1 rounded border border-[#323232] bg-[#2b2b2b] px-2 py-1 font-mono text-[11px] text-[#e8e8e8] outline-none focus:border-[#5394ec] disabled:opacity-50"
          />
          <button
            type="button"
            onClick={submitCustom}
            disabled={!customUrl.trim() || busy}
            className="rounded bg-[#499c54] px-2 py-1 text-[11px] text-white hover:bg-[#5fb069] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Fetch
          </button>
        </div>
        <p className="mt-1.5 text-[10px] leading-snug text-[#808080]">
          GitHub <code>/blob/</code> URLs auto-rewrite to raw. Other hosts must allow CORS or the
          fetch will fail.
        </p>
      </div>
      <p className="mt-3 text-[10px] leading-relaxed text-[#808080]">
        Heads up: downloads are the <em>only</em> network calls YesQL makes. The file is written
        into browser storage; nothing about your queries or other databases is uploaded.
      </p>
    </div>
  );
}
